// ---------------------------------------------------------------------------
// Strawberry Gas caret (^) format parser — mirrors the in-game /ad markup.
//
// Colors were defined in the PRD:
//   ^0 / ^7 → #FFFFFF       ^1 → #FF4500       ^2 → #4ADE80
//   ^3 → #FACC15             ^4 → #1E40AF      ^5 → #38BDF8
//   ^6 → #A855F7             ^8 → #991B1B      ^9 → #E879F9
//
// Styling:
//   ^* bold   ^_ underline   ^~ strikethrough
//   ^= underline + strikethrough
//   ^r reset all formatting back to default white
//
// Codes can stack (e.g. ^*^= → bold + underline + strikethrough).
// Unknown codes are rendered literally; ^^ escapes a caret.
// ---------------------------------------------------------------------------

export const CARET_COLORS = {
  '0': '#FFFFFF',
  '1': '#FF4500',
  '2': '#4ADE80',
  '3': '#FACC15',
  '4': '#1E40AF',
  '5': '#38BDF8',
  '6': '#A855F7',
  '7': '#FFFFFF',
  '8': '#991B1B',
  '9': '#E879F9',
};

const DEFAULT_STYLE = { color: '#FFFFFF', bold: false, underline: false, strike: false };

/**
 * Parse raw caret-tagged text into styled segments.
 * @param {string} raw
 * @returns {Array<{text: string, color: string, bold: boolean, underline: boolean, strike: boolean}>}
 */
export function parseCaretText(raw = '') {
  const segments = [];
  let style = { ...DEFAULT_STYLE };
  let buffer = '';

  const flush = () => {
    if (buffer.length > 0) {
      segments.push({ text: buffer, ...style });
      buffer = '';
    }
  };

  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (ch !== '^') {
      buffer += ch;
      continue;
    }

    const next = raw[i + 1];
    // Trailing caret or escaped caret ("^^")
    if (next === undefined) {
      buffer += '^';
      continue;
    }
    if (next === '^') {
      buffer += '^';
      i += 1;
      continue;
    }

    flush();
    switch (next) {
      case 'r':
        style = { ...DEFAULT_STYLE };
        break;
      case '*':
        style = { ...style, bold: true };
        break;
      case '_':
        style = { ...style, underline: true };
        break;
      case '~':
        style = { ...style, strike: true };
        break;
      case '=':
        style = { ...style, underline: true, strike: true };
        break;
      default:
        if (Object.prototype.hasOwnProperty.call(CARET_COLORS, next)) {
          style = { ...style, color: CARET_COLORS[next] };
        } else {
          // Unknown/reserved code — render literally
          buffer += '^' + next;
        }
    }
    i += 1;
  }
  flush();

  return segments;
}

/** Combine text-decoration flags into a CSS text-decoration-line value */
export function decorationLine(style) {
  const parts = [];
  if (style.underline) parts.push('underline');
  if (style.strike) parts.push('line-through');
  return parts.join(' ') || 'none';
}

/** "$1,234" money formatting (whole-dollar register display) */
export function formatMoney(n) {
  return (
    '$' +
    Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })
  );
}

/** Clean integer formatting */
export function formatQty(n) {
  return Number(n || 0).toLocaleString('en-US');
}